import React, { useState } from 'react';
import { FileJson, FileSpreadsheet, FileText, Printer, Download, Mail } from 'lucide-react';
import { reportsAPI } from '../services/part2-api';
import { toast } from 'react-hot-toast';
import EmailReportModal from './EmailReportModal';

const ExportBar = ({ reportType, dateRange, businessId, reportName }) => {
  const [exporting, setExporting] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const handleExport = async (format) => {
    try {
      setExporting(true);

      const response = await reportsAPI.exportReport({
        report_type: reportType,
        format: format,
        from_date: dateRange?.from_date,
        to_date: dateRange?.to_date
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const filename = response.headers['content-disposition']
        ? response.headers['content-disposition'].split('filename=')[1].replace(/"/g, '')
        : `${reportType}_${dateRange?.from_date || 'all'}_${dateRange?.to_date || 'all'}.${format}`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-3 mb-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 font-medium">
          Export Options
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleExport('json')}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            title="Export as JSON"
          >
            <FileJson className="w-3.5 h-3.5" />
            <span>JSON</span>
          </button>
          
          <button
            onClick={() => handleExport('excel')}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            title="Export as Excel"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            <span>Excel</span>
          </button>
          
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            title="Export as PDF"
          >
            <FileText className="w-3.5 h-3.5" />
            <span>PDF</span>
          </button>
          
          <div className="w-px h-5 bg-gray-300"></div>
          
          <button
            onClick={() => setEmailModalOpen(true)}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-teal-500 to-cyan-500 rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-colors disabled:opacity-50"
            title="Email Report"
          >
            <Mail className="w-3.5 h-3.5" />
            <span>Email</span>
          </button>
          
          <button
            onClick={handlePrint}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            title="Print"
          >
            <Printer className="w-3.5 h-3.5" />
            <span>Print</span>
          </button>
        </div>
      </div>
      
      <EmailReportModal
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        reportType={reportType}
        dateRange={dateRange}
        businessId={businessId}
        reportName={reportName || reportType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      />
    </div>
  );
};

export default ExportBar;
